import React, { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Col, message, Row } from "antd";
import "./CartPublic.less";
import ModalConfirmSelect from "commons/components/Modal/ModalConfirmSelect";
import useGetRealTimeParkinglot from "signalr/useGetRealTimeParkinglot";
import { PropertyStatus } from "constraints/PropertyEnum";
import services from "commons/apis/index";
import { useTranslation } from "react-i18next";
import SplashScreen from "commons/components/publicCart/SplashScreen/SplashScreen";
import SliderCarousel from "commons/components/common/SliderCarousel";
import {
  convertToS3Link,
  formatSecondsToMinutes,
  mapAndTranslateKey,
} from "commons/utils/function";
import UnNavHeader from "commons/components/UnNavHeader/UnNavHeader";
import TickingSoundPlayer from "commons/components/common/TickingSoundPlayer";

export const CartPublic = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parking_token = searchParams.get("parking_token");
  const booking_no = searchParams.get("booking_no");
  const { t, i18n } = useTranslation();
  const [countdown, setCountdown] = useState(9999);
  const [startChoose, setStartChoose] = useState(false);
  const [interactionLocked, setInteractionLocked] = useState(false);
  const [choosed, setChoosed] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isDisconnected, setIsDisconnected] = useState(false);
  const countdownIntervalRef = useRef(null);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [project, setProject] = useState({});
  const [searchString, setSearchString] = useState("");
  const {
    data: realtimeData,
    error,
    errorMessage,
    disconnect,
    connected,
  } = useGetRealTimeParkinglot({
    projectId: project.projectId,
    parkingToken: parking_token,
    bookingNo: booking_no,
    startChoose,
  });
  const countdownStartTimeRef = useRef(null);
  const totalTimeRef = useRef(0);

  // === Countdown setup ===
  useEffect(() => {
    window.addEventListener("beforeunload", handleUserExit);

    return () => {
      window.removeEventListener("beforeunload", handleUserExit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProjectId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parking_token, booking_no]);

  const fetchProjectId = async () => {
    if (!parking_token || !booking_no) return false;

    try {
      const result = await services.checkTokenParkingSelection({
        token: parking_token,
        bookingNo: booking_no,
      });

      if (!result || !result.projectId) {
        message.error(result?.message);
        setIsFetching(false);
        return false;
      }

      if (result.isExistConnection) {
        setIsDisconnected(result.isExistConnection);
        setIsFetching(false);
        return false;
      }

      setProject({
        ...result,
        ...result.property,
        projectId: result?.projectId,
        projectName: result?.projectName,
        status: result?.property?.status,
      });
      setCountdown(result.totalTimeConnect);
      totalTimeRef.current = result.totalTimeConnect;
      countdownStartTimeRef.current = Date.now();

      if (result.totalTimeConnect !== result.stackingPlanTotalTimeInSeconds) {
        setStartChoose(true);
        startCountdown();
      }

      setIsFetching(false);
      return true;
    } catch (err) {
      message.error(t("cart.check_token_error"));
      setIsFetching(false);
      return false;
    }
  };

  const startCountdown = () => {
    if (countdownIntervalRef.current) return;

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - countdownStartTimeRef.current) / 1000
      );
      const remaining = totalTimeRef.current - elapsed;

      if (remaining <= 0) {
        message.error(t("cart.timeout"));
        clearCountdownInterval();
        disconnect();
        setCountdown(0);
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  };

  const clearCountdownInterval = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
      setInteractionLocked(true);
    }
  };

  const handleUserExit = () => {
    clearCountdownInterval();
  };

  const handleCellClick = (aptData) => {
    if (interactionLocked) return t("cart.timeout");
    switch (aptData?.status) {
      case PropertyStatus.SELECTED:
        message.error(
          t("cart.spot_already_sold", { propertyNo: aptData?.propertyNo })
        );
        break;
      case PropertyStatus.AVAILABLE:
        setSelectedApartment(aptData);
        setModalVisible(true);
        break;
      default:
        message.error(
          t("cart.spot_not_for_sale", { propertyNo: aptData?.propertyNo })
        );
        break;
    }
  };

  const handleConfirmSelect = async () => {
    const response = await services.selectionForCustomer({
      propertyId: selectedApartment?.id,
      token: parking_token,
      bookingNo: booking_no,
    });
    if (response.success) {
      message.success(
        t("cart.select_success", { propertyNo: selectedApartment?.propertyNo })
      );
      window.location.reload();
      setModalVisible(false);
      setChoosed(true);
      setSelectedApartment(null);
    } else {
      const messageKey = mapAndTranslateKey(response?.response);
      message.error(
        t(messageKey ?? "backendKey.ParkingHasBeenSelection", {
          propertyNo: selectedApartment?.propertyNo,
        })
      );
      // message.error(t("cart.select_failed", { propertyNo: selectedApartment?.propertyNo }));
    }
  };

  // === Handle start choosing ===
  const handleStartChoose = async () => {
    const result = await fetchProjectId(); // Thêm dòng này để kiểm tra lại kết nối
    if (!result) return; // Nếu token không hợp lệ hoặc đã có thiết bị khác đang kết nối

    if (error) {
      message.error(t("cart.token_invalid"));
    } else {
      setStartChoose(true);
      startCountdown();
    }
  };

  const renderParkingArea = (data) => {
    return (
      <div className="parking-container">
        {data?.map((areaData, index) => {
          const imageGallery = [];
          // eslint-disable-next-line array-callback-return
          areaData?.parkingAreaFiles?.map((item) => {
            imageGallery.push(convertToS3Link(item.fileName));
          });
          return (
            <div key={index} className="parking-area">
              <h4 className="area-title">
                {t("modal.sendmail.floor")} {areaData?.parkingAreaName}
              </h4>
              <Row gutter={[16, 16]}>
                <Col className="image-col" lg={6} md={24} sm={24} span={24}>
                  <SliderCarousel imageGallery={imageGallery} />
                </Col>
                <Col className="content-col" lg={18} md={24} sm={24} span={24}>
                  {renderParkingBlock(
                    areaData?.properties,
                    areaData?.parkingAreaName
                  )}
                </Col>
              </Row>
            </div>
          );
        })}

        {getErrorMessage && (
          <div className="interaction-overlay">
            <div className="interaction-text">{getErrorMessage}</div>
          </div>
        )}
      </div>
    );
  };

  const renderParkingBlock = (areaData, parkingAreaName) => {
    const filteredData = areaData?.filter((item) =>
      searchString
        ? item?.propertyNo?.toLowerCase().includes(searchString.toLowerCase())
        : true
    );

    return (
      <div className="parking-grid">
        {filteredData?.map((item, index) => {
          const className = getStatusClass(item?.status);
          if (className === "not-for-sale") {
            return null;
          }
          return (
            <div
              key={`${parkingAreaName}-${index}`}
              className={`parking-spot ${className}`}
              onClick={() => {
                handleCellClick({ ...item, parkingAreaName });
              }}
            >
              {/* <div className="car-icon">🚗</div> */}
              <div className="spot-number">{item?.propertyNo}</div>
              {/* {item?.property.code && <div className="spot-code">{item.code}</div>} */}
            </div>
          );
        })}
      </div>
    );
  };

  const getStatusClass = (status) => {
    if (status === PropertyStatus.AVAILABLE) return "available";
    if (status === PropertyStatus.SELECTED) return "occupied";
    return "not-for-sale";
  };

  const handleCancleChoosing = () => {
    setModalVisible(false);
  };

  const getErrorMessage = useMemo(() => {
    if (interactionLocked) {
      handleCancleChoosing();
      return t("cart.timeout");
    }
    if (choosed || project?.status === PropertyStatus.SELECTED)
      return t("cart.already_selected");
    if (errorMessage) {
      handleCancleChoosing();
      return t(errorMessage);
    }
    if (!!!parking_token || !!!booking_no || !!!project?.projectId || error) {
      handleCancleChoosing();
      return t("cart.token_invalid");
    }
    if (startChoose && !connected) {
      handleCancleChoosing();
      return t("cart.connection_lost");
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    errorMessage,
    parking_token,
    booking_no,
    choosed,
    interactionLocked,
    project,
    error,
    connected,
    startChoose,
    i18n.language,
  ]);

  const handleConfirmDisconnect = async () => {
    const response = await services.sendMessageDisconnectClientsByToken({
      parkingToken: parking_token,
    });
    // const response = await services.sendMessageDisconnectClientsByTokenDemo({ parkingToken: parking_token });
    if (response.success) {
      message.success(t("cart.disconnect_success"));

      // Reset state để đảm bảo UI re-render đúng
      setChoosed(false);
      setInteractionLocked(false);
      setStartChoose(false);
      setCountdown(999);
      setSelectedApartment(null);
      setModalVisible(false);
    }
    // Re-fetch
    await fetchProjectId();
  };

  return (
    <Fragment>
      <div className="container-fix parking-container-public">
        <div className="body-container">
          <UnNavHeader />
          <div
            className="main dark-bg animate__animated animate__fadeIn"
            id="list-room-page"
          >
            <div className="container-fluid">
              {!startChoose ? (
                <SplashScreen
                  project={project}
                  getErrorMessage={getErrorMessage}
                  isDisconnected={isDisconnected}
                  isFetching={isFetching}
                  countdown={countdown}
                  handleStartChoose={handleStartChoose}
                  handleConfirmDisconnect={handleConfirmDisconnect}
                />
              ) : (
                <>
                  {/* Countdown instead of lucky draw */}
                  <div className="countdown-search-wrapper">
                    <div className="countdown-box">
                      {getErrorMessage ? (
                        <span className="expired-text">{getErrorMessage}</span>
                      ) : (
                        <span
                          className={`countdown-text ${
                            countdown <= 30 ? "danger" : "warning"
                          }`}
                        >
                          <span className="label">
                            {t("cart.remaining_time")}:
                          </span>{" "}
                          {formatSecondsToMinutes(countdown)}{" "}
                          {t("cart.minutes")}
                        </span>
                      )}
                    </div>
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder={
                          t("cart.search_placeholder") ||
                          "Tìm theo mã chỗ để..."
                        }
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  </div>

                  <div className="wrapper-header">
                    <div className="note">
                      <ul>
                        {/* <li><span></span> {t('cart.not_for_sale')}</li> */}
                        <li className="available">
                          <span></span> {t("cart.available")}
                        </li>
                        <li className="sold">
                          <span></span> {t("cart.sold")}
                        </li>
                      </ul>
                    </div>
                    <div className="blocks">
                      <div>
                        <strong>{t("cart.bookingNo")} </strong>{" "}
                        {project.bookingCustomId}
                      </div>
                      <div>
                        <strong>{t("cart.order")} </strong>{" "}
                        {project.orderNumber}
                      </div>
                      <div className="block-project">
                        <strong>{t("cart.project")} </strong>{" "}
                        {project.projectName}
                      </div>
                    </div>
                  </div>
                  {renderParkingArea(realtimeData)}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ModalConfirmSelect
        visible={modalVisible}
        onConfirm={handleConfirmSelect}
        onCancel={() => setModalVisible(false)}
        slotData={selectedApartment}
      />
      <TickingSoundPlayer
        countdown={countdown}
        playing={startChoose && !interactionLocked && !choosed}
        startTickAt={30}
        fastTickAt={30}
      />
    </Fragment>
  );
};
